"use client";

import * as React from "react";
import { Search as SearchIcon } from "lucide-react";

import { CardContent } from "@/components/ui/card";
import { usePersonation } from "@/lib/personation-context";
import { applyLinkRegexReplaces } from "@/lib/url-utils";
import type { Source } from "@/components/api/fiinder-api-types";
import { useSourceNavigation } from "@/lib/use-source-navigation";

import { useWidgetMode } from "./widget-mode-context";

function getFaviconUrl(url: string): string {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch {
    return "";
  }
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

type SourceCardProps = {
  source: Source;
  index: number;
  onClick: () => void;
};

function SourceCard({ source, index, onClick }: SourceCardProps) {
  const { openSearchWithQuery } = useWidgetMode();
  const { linkRegexReplaces } = usePersonation();
  // Use description with fallback to snippet for backwards compatibility
  const displayText = source.description || source.snippet;

  const transformedUrl = React.useMemo(
    () => applyLinkRegexReplaces(source.url, linkRegexReplaces),
    [source.url, linkRegexReplaces]
  );

  return (
    <div
      className="border rounded-lg p-4 cursor-pointer transition-colors hover:bg-muted/60"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`source-card-${index}`}
    >
      <div className="flex gap-4">
        {/* Thumbnail - use og_image if available */}
        <div className="w-20 h-20 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
          <img
            src={source.og_image || "https://onboxx.app/static-images/placeholder.png"}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://onboxx.app/static-images/placeholder.png";
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Domain with favicon + index */}
          <div className="flex items-center gap-1.5 mb-1">
            <img
              src={getFaviconUrl(transformedUrl)}
              alt=""
              className="w-4 h-4 rounded-sm flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <span className="text-xs text-muted-foreground truncate">
              {getDomain(transformedUrl)}
            </span>
            <span className="text-xs text-muted-foreground font-medium">
              [{index + 1}]
            </span>
          </div>

          {/* Title */}
          <h3 className="font-semibold text-sm mb-1 line-clamp-2">
            {source.title || getDomain(transformedUrl)}
          </h3>

          {/* Description (with fallback to snippet) */}
          {displayText && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {displayText}
            </p>
          )}

          {/* Keywords */}
          {source.keywords && source.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {source.keywords.slice(0, 5).map((keyword, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    openSearchWithQuery(keyword);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 rounded-full transition-colors cursor-pointer"
                  data-testid={`source-keyword-pill-${idx}`}
                >
                  <SearchIcon className="w-3 h-3 flex-shrink-0" />
                  <span>{keyword}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type QueriesSectionProps = {
  queries: string[];
  onQueryClick?: (query: string) => void;
};

function QueriesSection({ queries, onQueryClick }: QueriesSectionProps) {
  const { labels } = usePersonation();

  if (!queries || queries.length === 0) {
    return null;
  }

  return (
    <div
      className="mb-5"
      data-testid="sources-queries-section"
    >
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        {labels.sources.searching}
      </p>
      <div className="flex flex-wrap gap-2">
        {queries.map((query, idx) => (
          <button
            type="button"
            key={`${query}-${idx}`}
            onClick={() => onQueryClick?.(query)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground rounded-full text-sm transition-colors cursor-pointer"
            data-testid={`query-pill-${idx}`}
          >
            <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{query}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SourcesBody() {
  const { mode, sourcesData, openSearchWithQuery } = useWidgetMode();
  const { labels } = usePersonation();
  const { navigateToSource } = useSourceNavigation();

  const handleSourceClick = React.useCallback(
    (source: Source) => {
      navigateToSource({ source });
    },
    [navigateToSource]
  );

  const handleQueryClick = React.useCallback(
    (query: string) => {
      openSearchWithQuery(query);
    },
    [openSearchWithQuery]
  );

  if (mode !== "sources" || !sourcesData) {
    return null;
  }

  const { sources, queries } = sourcesData;

  return (
    <CardContent
      className="flex flex-col flex-1 min-h-0 p-6 overflow-hidden"
      data-testid="sources-body"
    >
      <div className="flex-1 overflow-y-auto -mr-6 pr-6 pb-6">
        <QueriesSection queries={queries || []} onQueryClick={handleQueryClick} />

        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          {labels.sources.reading}
        </p>
        <div className="space-y-3" data-testid="sources-list">
          {sources.map((source, idx) => (
            <SourceCard
              key={`${source.url}-${source.title ?? ""}-${idx}`}
              source={source}
              index={idx}
              onClick={() => handleSourceClick(source)}
            />
          ))}
        </div>

        {sources.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="text-muted-foreground text-center">
              {labels.sources.noSourcesAvailable}
            </p>
          </div>
        )}
      </div>
    </CardContent>
  );
}

SourcesBody.displayName = "SourcesBody";
